import { useQuery } from '@tanstack/react-query';
import { Separator } from '@vta/ui';
import type { Health } from '@vta/types';
import { apiClient } from '../../lib/api-client.ts';
import { useDemoStore } from '../../stores/demo-store.ts';

/**
 * Footer strip (PRD §9.2):
 *   [last synced]  ·  [unofficial-project disclaimer]  ·  [imagery credit]
 *
 * Two registers:
 *   - default: static bar under page content.
 *   - floating (atlas mode): floats over the globe on a bottom gradient
 *     scrim; also carries the satellite-imagery attribution required by the
 *     tile provider's terms.
 *
 * `lastSyncedAt` comes from `/api/health` (which reads `sync_runs`). When
 * the API is unreachable the field renders as "—" — never "undefined".
 *
 * The disclaimer text is the verbatim copy from PRD §1.3 — do not edit.
 */
const DISCLAIMER =
  'VEX Teams Atlas is an independent, unofficial project and is not affiliated with or endorsed by VEX Robotics, REC Foundation, or RobotEvents.';

const IMAGERY_CREDIT = 'Imagery © Esri, Maxar, Earthstar Geographics';

export function FooterStrip({ floating = false }: { floating?: boolean }) {
  const demo = useDemoStore((s) => s.active);
  const health = useQuery<Health>({
    queryKey: ['health'],
    queryFn: ({ signal }) => apiClient.get<Health>('/api/health', { signal, anonymous: true }),
    staleTime: 5 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
    retry: 0,
  });

  const lastSynced = formatLastSynced(health.data?.lastSyncedAt);

  return (
    <footer
      className={
        floating
          ? 'absolute inset-x-0 bottom-0 z-sticky bg-gradient-to-t from-bg-sunken/90 via-bg-sunken/50 to-transparent px-4 pb-2 pt-5 md:px-6'
          : 'z-sticky shrink-0 border-t border-border-subtle bg-bg-base/85 px-4 py-2 backdrop-blur-md md:px-6'
      }
      role="contentinfo"
    >
      <div className="flex flex-col gap-1 text-2xs text-fg-subtle md:flex-row md:items-center md:gap-3">
        <span className="inline-flex items-center gap-1.5">
          <span className="font-mono uppercase tracking-[0.18em] text-fg-muted">last sync</span>
          <time dateTime={health.data?.lastSyncedAt ?? undefined}>
            {demo ? 'demo' : lastSynced}
          </time>
        </span>
        <Separator orientation="vertical" className="hidden h-3 md:block" decorative />
        <p className="md:flex-1 md:truncate" title={DISCLAIMER}>
          {DISCLAIMER}
        </p>
        {floating ? (
          <>
            <Separator orientation="vertical" className="hidden h-3 md:block" decorative />
            <span className="hidden whitespace-nowrap md:inline">{IMAGERY_CREDIT}</span>
          </>
        ) : null}
      </div>
    </footer>
  );
}

function formatLastSynced(iso: string | null | undefined): string {
  if (!iso) return '—';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '—';
  const diffMs = Date.now() - date.getTime();
  const minutes = Math.round(diffMs / 60_000);
  if (minutes < 1) return 'just now';
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.round(minutes / 60);
  if (hours < 48) return `${hours}h ago`;
  const days = Math.round(hours / 24);
  return `${days}d ago`;
}
